import React, { useState } from 'react'
import { useDebounceValue } from '@sketch/utils'
import {
  Flex,
  Box,
  Text,
  LoadingPlaceholder,
  Checkbox,
  handleFetchMore,
  InfiniteList,
  Input,
} from '@sketch/components'
import { FetchMoreFunc } from '@sketch/components/src/InfiniteList/InfiniteList'
import {
  ListContainer,
  PageIcon,
  AllPrototypesIcon,
  PrototypeIcon,
  SearchContainer,
  SearchActions,
  SearchButton,
} from './ContentsTab.styles'
import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  useGetPagesQuery,
  useGetFramesForDocumentQuery,
} from '@sketch/gql-types'
import { usePreviewForm } from './usePreviewForm'
import { capitalize } from 'lodash'

interface ContentsTabProps {
  shareIdentifier: string
  versionShortId: string
}

const ENTRIES_PATH = ['share', 'version', 'document', 'pages', 'entries']

type ListItem = { name: string; uuid: string }
type SelectedItem = { uuid: string; type: 'PAGE' | 'PROTOTYPE' }

interface ItemListProps {
  handleSelectAll: () => void
  toggleUUID: (id: string, type: 'PAGE' | 'PROTOTYPE') => void
  items: ListItem[]
  after: string | null
  fetchMore: FetchMoreFunc
  selectedUUIDs: SelectedItem[]
  allChecked: boolean
  type: 'prototypes' | 'pages'
}

const ItemList = ({
  handleSelectAll,
  items,
  after,
  fetchMore,
  toggleUUID,
  allChecked,
  selectedUUIDs,
  type,
}: ItemListProps) => {
  if (items.length === 0) {
    // @TODO: add empty state
    return <>No {type} found</>
  }

  return (
    <>
      <Box mb={2}>
        <Checkbox
          name={`all${type}`}
          checked={allChecked}
          onChange={handleSelectAll}
        >
          <Flex alignItems="center">
            {type === 'pages' ? <PageIcon /> : <AllPrototypesIcon />}
            All {capitalize(type)}
          </Flex>
        </Checkbox>
      </Box>
      <ListContainer>
        <InfiniteList
          wrappingComponent={React.Fragment}
          renderLoading={() => <LoadingPlaceholder size="64px" />}
          canLoadMore={after !== null}
          onLoadMore={handleFetchMore(fetchMore, ENTRIES_PATH, {
            dataIdFromObject,
            after: after,
          })}
        >
          {items.map(({ uuid, name }) =>
            uuid !== null ? (
              <Box mb={2} key={uuid}>
                <Checkbox
                  name="selectedUUIDs"
                  checked={selectedUUIDs.some(item => item.uuid === uuid)}
                  onChange={() =>
                    toggleUUID(uuid, type === 'pages' ? 'PAGE' : 'PROTOTYPE')
                  }
                >
                  <Flex alignItems="center">
                    {type === 'pages' ? <PageIcon /> : <PrototypeIcon />}
                    {name}
                  </Flex>
                </Checkbox>
              </Box>
            ) : null
          )}
        </InfiniteList>
      </ListContainer>
    </>
  )
}

export const ContentsTab = ({
  shareIdentifier,
  versionShortId,
}: ContentsTabProps) => {
  const { values, setFieldValue } = usePreviewForm()
  const selectedUUIDs: SelectedItem[] = values.selectedUUIDs || []
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounceValue(searchTerm, 300)

  const { data, loading, error, fetchMore } = useGetPagesQuery({
    variables: {
      shareIdentifier,
      versionShortId,
      search: debouncedSearchTerm,
    },
  })

  const prototypeFramesQuery = useGetFramesForDocumentQuery({
    variables: {
      identifier: shareIdentifier,
      versionShortId,
      search: debouncedSearchTerm,
      filter: 'PROTOTYPE',
    },
  })

  const handleAddUUID = (id: string, type: 'PAGE' | 'PROTOTYPE') => {
    if (!selectedUUIDs.some(item => item.uuid === id)) {
      setFieldValue('selectedUUIDs', [...selectedUUIDs, { uuid: id, type }])
    }
  }

  const handleRemoveUUID = (id: string) => {
    setFieldValue(
      'selectedUUIDs',
      selectedUUIDs.filter(item => item.uuid !== id)
    )
  }

  const toggleUUID = (id: string, type: 'PAGE' | 'PROTOTYPE') => {
    if (selectedUUIDs.some(item => item.uuid === id)) {
      handleRemoveUUID(id)
    } else {
      handleAddUUID(id, type)
    }
  }

  const handleSelectAllPages = () => {
    const areAllFilteredPagesSelected = allPageIds.every(id =>
      selectedUUIDs.some(item => item.uuid === id)
    )
    if (areAllFilteredPagesSelected) {
      // Deselect filtered pages
      setFieldValue(
        'selectedUUIDs',
        selectedUUIDs.filter(item => !allPageIds.includes(item.uuid))
      )
    } else {
      // Select all filtered pages (keep existing prototypes and other pages)
      const newPageItems = allPageIds
        .filter(id => !selectedUUIDs.some(item => item.uuid === id))
        .map(id => ({ uuid: id, type: 'PAGE' as const }))
      setFieldValue('selectedUUIDs', [...selectedUUIDs, ...newPageItems])
    }
  }

  const handleSelectAllPrototypes = () => {
    const areAllPrototypesSelected = allPrototypeIds.every(id =>
      selectedUUIDs.some(item => item.uuid === id)
    )
    if (areAllPrototypesSelected) {
      // Deselect all prototypes
      setFieldValue(
        'selectedUUIDs',
        selectedUUIDs.filter(item => !allPrototypeIds.includes(item.uuid))
      )
    } else {
      // Select all prototypes (keep existing pages)
      const newPrototypeItems = allPrototypeIds
        .filter(id => !selectedUUIDs.some(item => item.uuid === id))
        .map(id => ({ uuid: id, type: 'PROTOTYPE' as const }))
      setFieldValue('selectedUUIDs', [...selectedUUIDs, ...newPrototypeItems])
    }
  }

  const handleGlobalSelectAll = () => {
    const allItems = [
      ...allPageIds.map(id => ({ uuid: id, type: 'PAGE' as const })),
      ...allPrototypeIds.map(id => ({
        uuid: id,
        type: 'PROTOTYPE' as const,
      })),
    ]
    setFieldValue('selectedUUIDs', allItems)
  }

  const handleGlobalDeselectAll = () => {
    setFieldValue('selectedUUIDs', [])
  }

  if (error || prototypeFramesQuery.error) {
    // @TODO: add error case
    return (
      <>
        <Text mb={3}>
          Select the prototypes or pages that will be available in this preview.
        </Text>
        <Flex justifyContent="center" height={543}>
          An error occured
        </Flex>
      </>
    )
  }

  const afterPages = data?.share?.version?.document?.pages?.meta.after || null
  const afterPrototypes =
    prototypeFramesQuery.data?.share?.version?.document?.frames.meta.after ||
    null

  const formatEntry = (entries: any[]) =>
    entries
      .map(entry => ({
        name: entry.name,
        uuid: entry.uuid,
      }))
      .filter(
        (page): page is ListItem => page.name !== null && page.uuid !== null
      ) ?? []

  const pages = formatEntry(
    data?.share?.version?.document?.pages?.entries || []
  )
  const prototypes = formatEntry(
    prototypeFramesQuery.data?.share?.version?.document?.frames.entries || []
  )

  const allPageIds = pages.map(entry => entry.uuid)
  const allPrototypeIds = prototypes.map(entry => entry.uuid)

  const isLoading = !!(loading || prototypeFramesQuery.loading)

  return (
    <>
      <Text mb={3}>
        Select the prototypes or pages that will be available in this preview.
      </Text>

      <SearchContainer>
        <Input
          type="text"
          placeholder="Search…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <SearchActions>
          <SearchButton
            loading={isLoading}
            variant="primary"
            disabled={
              selectedUUIDs.length ===
              allPageIds.length + allPrototypeIds.length
            }
            onClick={handleGlobalSelectAll}
          >
            Select All
          </SearchButton>
          <SearchButton
            loading={isLoading}
            disabled={selectedUUIDs.length === 0}
            variant="primary"
            onClick={handleGlobalDeselectAll}
          >
            Deselect All
          </SearchButton>
        </SearchActions>
      </SearchContainer>

      {isLoading ? (
        <Flex justifyContent="center" height={384}>
          <LoadingPlaceholder />
        </Flex>
      ) : (
        <Box height={380}>
          <Box mt={5}>
            <ItemList
              type="prototypes"
              handleSelectAll={handleSelectAllPrototypes}
              toggleUUID={toggleUUID}
              fetchMore={fetchMore}
              items={prototypes}
              selectedUUIDs={selectedUUIDs}
              after={afterPrototypes}
              allChecked={allPrototypeIds.every(id =>
                selectedUUIDs.some(item => item.uuid === id)
              )}
            />
          </Box>
          <Box mt={5}>
            <ItemList
              type="pages"
              handleSelectAll={handleSelectAllPages}
              toggleUUID={toggleUUID}
              fetchMore={fetchMore}
              items={pages}
              selectedUUIDs={selectedUUIDs}
              after={afterPages}
              allChecked={allPageIds.every(id =>
                selectedUUIDs.some(item => item.uuid === id)
              )}
            />
          </Box>
        </Box>
      )}
    </>
  )
}
